/**
 * [이용권 부여 다이얼로그] — master / manager
 *
 * 목적: 결제 없이 관리자가 직접 모던워시 이용권(멤버십)을 지급한다.
 *       레거시 잔여기간 이관, 보상 지급, 프로모션·체험 제공 등.
 *
 * 백엔드: POST /api/v1/subscriptions/admin/subscriptions/grant
 *   body: { userId, vehicleId, productId, startDate, endDate, grantReason, evidenceNote?, storeId? }
 *   → manualGrant:true 구독 생성 (카드 없음, 자동결제 없음)
 *   → 종료일 도달 시 09:00 스케줄러가 결제 시도 없이 cancelled 처리
 *   → SubscriptionHistory(granted) + AdminAuditLog(membership.grant) 이중 기록
 *
 * 날짜 처리 — 반드시 KST 경계로 보낸다:
 *   시작일 = 해당 날짜 00:00:00 KST / 종료일 = 해당 날짜 23:59:59.999 KST
 *   종료일을 날짜만(=UTC 자정=09:00 KST) 보내면 종료일 당일 09:00 스케줄러가 바로 만료시켜
 *   "종료일 당일은 이용 가능"이라는 운영자 기대와 어긋난다.
 *
 * 사용:
 *   <GrantMembershipDialog open={...} onClose={...} onGranted={...} />                 회원 검색부터
 *   <GrantMembershipDialog open={...} member={member} ... />                           회원 고정 (회원 상세)
 */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import api from '../api/client';
import { segmentedControlSx } from '../styles/uiTokens';
import { getAdminRole, isMaster } from '../utils/auth';

// backend/src/common/products.ts 와 동일 — 지급 가능한 구독 상품
export const GRANTABLE_PRODUCTS = [
  { id: 'daily_subscription', name: '모던워시 데일리', price: 49500 },
  { id: 'all_in_one_subscription', name: '모던워시 올인원', price: 54500 },
];

/**
 * 이용권 지급 가능 여부 — 백엔드 @Roles('master','manager') 와 동일 조건.
 * staff/readonly 관리자에게 눌러도 403 나는 버튼을 보여주지 않기 위해 사용.
 * (UI 게이트는 UX용이고 실제 차단은 백엔드 RolesGuard가 한다)
 */
export const canGrantMembership = () => ['master', 'manager'].includes(getAdminRole());

const PERIOD_PRESETS = [
  { key: '1m', label: '1개월', months: 1 },
  { key: '3m', label: '3개월', months: 3 },
  { key: '6m', label: '6개월', months: 6 },
  { key: '12m', label: '12개월', months: 12 },
];

const KST_OFFSET = '+09:00';

/** Date → 'YYYY-MM-DD' (input[type=date] 값) */
const toDateInput = (date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const todayStr = () => toDateInput(new Date());

/**
 * 'YYYY-MM-DD' + N개월 → 'YYYY-MM-DD'
 * 말일 보정: 1/31 +1개월 → 2/31(=3/3)이 되지 않도록 해당 월 말일로 클램프.
 * 종료일은 "지급 마지막 날"이므로 하루를 뺀다 (8/5 시작 1개월 → 9/4 종료 = 31일간).
 */
const addMonthsAsPeriodEnd = (dateStr, months) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return dateStr;
  const target = new Date(y, m - 1 + months, d);
  if (target.getDate() !== d) target.setDate(0); // 말일 클램프
  target.setDate(target.getDate() - 1); // 종료일 = 마지막 이용일
  return toDateInput(target);
};

/** 시작일~종료일 이용 일수 (양끝 포함) */
const inclusiveDays = (startStr, endStr) => {
  const start = new Date(`${startStr}T00:00:00`);
  const end = new Date(`${endStr}T00:00:00`);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
  return Math.round((end - start) / 86400000) + 1;
};

const toKstStartIso = (dateStr) => `${dateStr}T00:00:00.000${KST_OFFSET}`;
const toKstEndIso = (dateStr) => `${dateStr}T23:59:59.999${KST_OFFSET}`;

/** 백엔드 에러 메시지 정규화 (ValidationPipe는 배열로 준다) */
const errorMessage = (err, fallback) => {
  const msg = err?.response?.data?.message;
  if (Array.isArray(msg)) return msg.join('\n');
  return msg || fallback;
};

const GrantMembershipDialog = ({ open, onClose, onGranted, member: initialMember = null }) => {
  const master = isMaster();
  const memberLocked = !!initialMember;

  // ── 회원 선택 ──
  const [member, setMember] = useState(initialMember);
  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);

  // ── 대상 회원 부가 정보 ──
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState('');
  const [memberLoading, setMemberLoading] = useState(false);
  const [activeSub, setActiveSub] = useState(null); // 이미 이용 중인 멤버십 (있으면 지급 차단)

  // ── 지급 내용 ──
  const [productId, setProductId] = useState(GRANTABLE_PRODUCTS[0].id);
  const [startDate, setStartDate] = useState(todayStr());
  const [endDate, setEndDate] = useState(addMonthsAsPeriodEnd(todayStr(), 1));
  const [periodPreset, setPeriodPreset] = useState('1m');
  const [storeId, setStoreId] = useState('');
  const [stores, setStores] = useState([]);
  const [grantReason, setGrantReason] = useState('');
  const [evidenceNote, setEvidenceNote] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // 다이얼로그를 열 때마다 초기화 — 이전 지급 내용이 남지 않게
  useEffect(() => {
    if (!open) return;
    const today = todayStr();
    setMember(initialMember);
    setSearchInput('');
    setSearchResults([]);
    setSearched(false);
    setVehicles([]);
    setVehicleId('');
    setActiveSub(null);
    setProductId(GRANTABLE_PRODUCTS[0].id);
    setStartDate(today);
    setPeriodPreset('1m');
    setEndDate(addMonthsAsPeriodEnd(today, 1));
    setStoreId('');
    setGrantReason('');
    setEvidenceNote('');
    setError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialMember?._id]);

  // 회원 확정 시 차량·기존 멤버십 조회 (409를 미리 막고 대표 차량을 기본 선택)
  useEffect(() => {
    if (!open || !member?._id) return;
    let cancelled = false;

    (async () => {
      setMemberLoading(true);
      try {
        const [vehicleRes, subRes] = await Promise.all([
          api.get(`/member/admin/members/${member._id}/vehicles`),
          api.get(`/member/admin/members/${member._id}/subscriptions`),
        ]);
        if (cancelled) return;
        const list = vehicleRes.data || [];
        setVehicles(list);
        setVehicleId((list.find((v) => v.isPrimary) || list[0])?._id || '');
        const subs = subRes.data || [];
        setActiveSub(
          subs.find((s) => s.status === 'active' || s.status === 'cancellation_requested') || null,
        );
      } catch (err) {
        if (cancelled) return;
        setVehicles([]);
        setVehicleId('');
        setError(errorMessage(err, '회원 정보를 불러오지 못했습니다.'));
      } finally {
        if (!cancelled) setMemberLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, member?._id]);

  // master는 지급 매장을 직접 고른다 (미선택 시 백엔드 기본 매장으로 귀속)
  useEffect(() => {
    if (!open || !master) return;
    let cancelled = false;
    api
      .get('/stores/admin')
      .then((res) => {
        if (!cancelled) setStores(res.data || []);
      })
      .catch(() => {
        if (!cancelled) setStores([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, master]);

  const handleSearch = async (e) => {
    e?.preventDefault();
    const keyword = searchInput.trim();
    if (!keyword) return;
    setSearching(true);
    setError('');
    try {
      const res = await api.get('/member/admin/members', {
        params: { page: 1, limit: 20, search: keyword },
      });
      setSearchResults(res.data?.members || []);
      setSearched(true);
    } catch (err) {
      setError(errorMessage(err, '회원 검색에 실패했습니다.'));
    } finally {
      setSearching(false);
    }
  };

  const handlePresetChange = (_, next) => {
    if (!next) return;
    setPeriodPreset(next);
    if (next === 'custom') return; // 종료일 직접 입력 유지
    const preset = PERIOD_PRESETS.find((p) => p.key === next);
    if (preset) setEndDate(addMonthsAsPeriodEnd(startDate, preset.months));
  };

  const handleStartDateChange = (value) => {
    setStartDate(value);
    if (periodPreset === 'custom') return;
    const preset = PERIOD_PRESETS.find((p) => p.key === periodPreset);
    if (preset) setEndDate(addMonthsAsPeriodEnd(value, preset.months));
  };

  // 종료일을 직접 만지면 프리셋에서 '직접 지정'으로 전환 (프리셋이 값을 덮어쓰지 않도록)
  const handleEndDateChange = (value) => {
    setEndDate(value);
    setPeriodPreset('custom');
  };

  const days = inclusiveDays(startDate, endDate);
  const endsInPast = endDate ? new Date(toKstEndIso(endDate)).getTime() <= Date.now() : false;
  const periodInvalid = !startDate || !endDate || days === null || days < 1;

  const blockingReason = (() => {
    if (!member) return '지급할 회원을 선택해주세요.';
    if (activeSub) return '이미 이용 중인 멤버십이 있어 지급할 수 없습니다. 기존 멤버십 종료 후 지급해주세요.';
    if (!memberLoading && vehicles.length === 0)
      return '등록된 차량이 없습니다. 회원이 앱에서 차량을 등록해야 이용권을 지급할 수 있습니다.';
    if (!vehicleId) return '지급 대상 차량을 선택해주세요.';
    if (periodInvalid) return '종료일은 시작일과 같거나 이후여야 합니다.';
    if (endsInPast) return '종료일이 이미 지났습니다. 남은 기간이 있어야 지급할 수 있습니다.';
    if (!grantReason.trim()) return '지급 사유를 입력해주세요.';
    return null;
  })();

  const handleSubmit = async () => {
    if (blockingReason) {
      setError(blockingReason);
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await api.post('/subscriptions/admin/subscriptions/grant', {
        userId: member._id,
        vehicleId,
        productId,
        startDate: toKstStartIso(startDate),
        endDate: toKstEndIso(endDate),
        grantReason: grantReason.trim(),
        ...(evidenceNote.trim() ? { evidenceNote: evidenceNote.trim() } : {}),
        ...(master && storeId ? { storeId } : {}),
      });
      // 닫기를 먼저 — onGranted 쪽에서 alert 같은 blocking UI를 띄워도 다이얼로그가 남지 않는다
      onClose?.();
      onGranted?.();
    } catch (err) {
      setError(errorMessage(err, '이용권 부여에 실패했습니다.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  const selectedProduct = GRANTABLE_PRODUCTS.find((p) => p.id === productId);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
        <CardMembershipIcon color="primary" />
        이용권 부여
      </DialogTitle>

      <DialogContent dividers sx={{ py: 2.5 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          결제 없이 이용권을 지급합니다. 카드 자동결제는 발생하지 않으며, 종료일이 지나면 자동으로
          종료됩니다. 지급 내역은 감사 로그에 기록됩니다.
        </Alert>

        {/* ── 1. 회원 ─────────────────────────────── */}
        <Typography variant="subtitle2" fontWeight={700} color="primary" sx={{ mb: 1 }}>
          1. 지급 대상 회원
        </Typography>

        {member ? (
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: 'center', justifyContent: 'space-between' }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={700}>
                  {member.memberNickname || member.memberName || '-'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                  {member.memberEmail || '-'} · {member.memberPhone || '-'}
                </Typography>
              </Box>
              {!memberLocked && (
                <Button size="small" onClick={() => setMember(null)} disabled={submitting}>
                  변경
                </Button>
              )}
            </Stack>
          </Paper>
        ) : (
          <Box sx={{ mb: 2 }}>
            <Box component="form" onSubmit={handleSearch} sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="이름, 이메일, 전화번호로 검색"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <Button type="submit" variant="contained" disabled={searching || !searchInput.trim()}>
                {searching ? '검색 중' : '검색'}
              </Button>
            </Box>

            {searched && searchResults.length === 0 && !searching && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                검색 결과가 없습니다.
              </Typography>
            )}

            {searchResults.length > 0 && (
              <Paper variant="outlined" sx={{ mt: 1, maxHeight: 220, overflowY: 'auto' }}>
                {searchResults.map((m) => (
                  <Box
                    key={m._id}
                    onClick={() => setMember(m)}
                    sx={{
                      px: 1.5,
                      py: 1,
                      cursor: 'pointer',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-of-type': { borderBottom: 'none' },
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {m.memberNickname || m.memberName || '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                      {m.memberEmail || '-'} · {m.memberPhone || '-'}
                    </Typography>
                  </Box>
                ))}
              </Paper>
            )}
          </Box>
        )}

        {member && memberLoading && (
          <Stack direction="row" spacing={1} sx={{ mb: 2, alignItems: 'center' }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              차량·멤버십 확인 중...
            </Typography>
          </Stack>
        )}

        {member && !memberLoading && activeSub && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            이미 이용 중인 멤버십이 있습니다 (
            {GRANTABLE_PRODUCTS.find((p) => p.id === activeSub.productId)?.name || activeSub.productId}
            {activeSub.expirationDate
              ? ` · ~${new Date(activeSub.expirationDate).toLocaleDateString('ko-KR')}`
              : ''}
            ). 회원당 이용 중인 멤버십은 1건만 유지되므로 지급할 수 없습니다.
          </Alert>
        )}

        {member && !memberLoading && !activeSub && vehicles.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            등록된 차량이 없습니다. 회원이 앱에서 차량을 먼저 등록해야 이용권을 지급할 수 있습니다.
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        {/* ── 2. 지급 내용 ─────────────────────────── */}
        <Typography variant="subtitle2" fontWeight={700} color="primary" sx={{ mb: 1.5 }}>
          2. 이용권 상품 · 차량
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>이용권 상품</InputLabel>
            <Select
              value={productId}
              label="이용권 상품"
              onChange={(e) => setProductId(e.target.value)}
              disabled={submitting}
            >
              {GRANTABLE_PRODUCTS.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name} ({p.price.toLocaleString()}원/월 상당)
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" fullWidth disabled={!member || vehicles.length === 0 || submitting}>
            <InputLabel>대상 차량</InputLabel>
            <Select
              value={vehicleId}
              label="대상 차량"
              onChange={(e) => setVehicleId(e.target.value)}
            >
              {vehicles.map((v) => (
                <MenuItem key={v._id} value={v._id}>
                  {v.licensePlate}
                  {v.isPrimary ? ' (대표)' : ''}
                  {v.model ? ` · ${v.model}` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        {master && stores.length > 0 && (
          <FormControl size="small" fullWidth sx={{ mb: 2 }}>
            <InputLabel>귀속 매장 (선택)</InputLabel>
            <Select
              value={storeId}
              label="귀속 매장 (선택)"
              onChange={(e) => setStoreId(e.target.value)}
              disabled={submitting}
            >
              <MenuItem value="">기본 매장</MenuItem>
              {stores.map((s) => (
                <MenuItem key={s._id} value={s._id}>
                  {s.name} ({s.code})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Divider sx={{ my: 2 }} />

        {/* ── 3. 기간 ─────────────────────────────── */}
        <Typography variant="subtitle2" fontWeight={700} color="primary" sx={{ mb: 1.5 }}>
          3. 이용 기간
        </Typography>

        <ToggleButtonGroup
          value={periodPreset}
          exclusive
          onChange={handlePresetChange}
          size="small"
          color="primary"
          sx={{ ...segmentedControlSx, mb: 2, flexWrap: 'wrap', rowGap: 0.75 }}
          disabled={submitting}
        >
          {PERIOD_PRESETS.map((p) => (
            <ToggleButton key={p.key} value={p.key}>
              {p.label}
            </ToggleButton>
          ))}
          <ToggleButton value="custom">직접 지정</ToggleButton>
        </ToggleButtonGroup>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ alignItems: 'flex-start' }}>
          <TextField
            label="시작일"
            type="date"
            size="small"
            fullWidth
            value={startDate}
            onChange={(e) => handleStartDateChange(e.target.value)}
            disabled={submitting}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label="종료일 (이 날짜까지 이용 가능)"
            type="date"
            size="small"
            fullWidth
            value={endDate}
            onChange={(e) => handleEndDateChange(e.target.value)}
            disabled={submitting}
            error={periodInvalid || endsInPast}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: startDate } }}
          />
        </Stack>

        <Box sx={{ mt: 1.25, mb: 2 }}>
          {periodInvalid ? (
            <Typography variant="caption" color="error">
              종료일은 시작일과 같거나 이후여야 합니다.
            </Typography>
          ) : endsInPast ? (
            <Typography variant="caption" color="error">
              종료일이 이미 지났습니다. 남은 기간이 있어야 지급할 수 있습니다.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 0.75, rowGap: 0.25 }}>
              <Typography variant="caption" color="text.secondary">이용 기간</Typography>
              <Typography variant="caption" color="primary.main" fontWeight={700}>
                총 {days}일 · {startDate} ~ {endDate} 23:59 (KST)
              </Typography>
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* ── 4. 사유 ─────────────────────────────── */}
        <Typography variant="subtitle2" fontWeight={700} color="primary" sx={{ mb: 1.5 }}>
          4. 지급 사유 (감사 기록)
        </Typography>

        <TextField
          label="지급 사유 (필수)"
          required
          fullWidth
          size="small"
          multiline
          minRows={2}
          value={grantReason}
          onChange={(e) => setGrantReason(e.target.value)}
          placeholder="예) 레거시 전주점 잔여기간 이관 / 세차기 고장 보상 / 프로모션 체험 지급"
          disabled={submitting}
          slotProps={{ htmlInput: { maxLength: 200 } }}
          helperText={`${grantReason.length}/200`}
          sx={{ mb: 1.5, '& textarea': { lineHeight: 1.5 } }}
        />

        <TextField
          label="증빙 메모 (선택)"
          fullWidth
          size="small"
          value={evidenceNote}
          onChange={(e) => setEvidenceNote(e.target.value)}
          placeholder="예) 레거시 회원번호 12345, 잔여 23일 확인"
          disabled={submitting}
          slotProps={{ htmlInput: { maxLength: 200 } }}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2, whiteSpace: 'pre-line' }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
        <Button onClick={handleClose} disabled={submitting}>
          취소
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || memberLoading || !!blockingReason}
        >
          {submitting
            ? '지급 중...'
            : `${selectedProduct?.name || '이용권'} ${days && days > 0 ? `${days}일` : ''} 지급`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GrantMembershipDialog;
